import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { getAttribution, attributionForPayload } from "@/lib/attribution";

// First-party server-side traffic beacon.
// - Fires ONCE per unique pathname per browsing session.
//   (Query-string / hash changes triggered by modals, marketplace routing,
//   consent banners, SPA replaceState calls, etc. must NOT be counted as
//   new pageviews.)
// - Sends NO cookies (credentials: "omit") and stores NO state on the client.
// - Skipped for /growing-circle/admin/* so the back-office does not inflate
//   its own traffic.
// - Does NOT require analytics consent: the server records ONLY aggregate
//   counters and a same-day pseudonym that rotates daily; raw IP is never
//   persisted. See /app/backend/analytics_tracker.py.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PageviewBeacon() {
  const location = useLocation();
  const lastPathRef = useRef(null);
  useEffect(() => {
    const path = location.pathname || "/";
    if (path.startsWith("/growing-circle/admin")) return;
    // Dedup: only fire when the pathname actually changes. This intentionally
    // ignores location.search / location.hash so modals and in-page anchors
    // never inflate the pageview counter.
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    const qs = new URLSearchParams(location.search || "");
    // Session-scoped attribution: captured on first landing, reused on
    // every subsequent SPA navigation so downstream conversions (Buy,
    // Amazon exit, Growing Circle registration) share the same ref/utm.
    const attribution = attributionForPayload();
    const payload = {
      path,
      referrer: (typeof document !== "undefined" && document.referrer) || null,
      utm_source: attribution.utm_source || qs.get("utm_source"),
      utm_medium: attribution.utm_medium || qs.get("utm_medium"),
      utm_campaign: attribution.utm_campaign || qs.get("utm_campaign"),
      ref: attribution.ref || qs.get("ref") || null,
    };
    try {
      fetch(`${BACKEND_URL}/api/analytics/pageview`, {
        method: "POST",
        credentials: "omit",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch {
      /* never break the page for analytics */
    }
  }, [location.pathname]);
  return null;
}
